<template>
  <div ref="recaptcha" :data-sitekey="siteKey" class="g-recaptcha" :class="[lang, location]" />
</template>

<script>
import { mapState } from 'vuex';

/** reCAPTCHA 스크립트 아이디 */
const RECAPTCHA_SCRIPT_ID = 'recaptcha-script';
/** reCAPTCHA script source */
const RECAPTCHA_SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js';
/** reCAPTCHA 스크립트 로드 실패 시 에러 클래스 */
class ReCaptchaError extends Error {
  constructor(message) {
    super(message);
    this.message = '[vue3-recaptcha-v2] ' + message;
    /** 스크립트 로드 실패 시 스크립트 제거 */
    document.getElementById(RECAPTCHA_SCRIPT_ID).remove();
  }
}
export default {
  props: {
    verify: {
      type: Function,
      required: true,
    },
    // captcha 삽입 위치 (nophone: 모달, embed: 페이지 내)
    // 값을 modal로 설정 시 정책 위반으로 사용 불가
    location: {
      type: String,
      default: 'embed',
    },
  },
  data() {
    return {
      siteKey: process.env.VUE_APP_RECAPTCHA, // Google에서 발급받은 site key
      captchaVerified: false, // reCAPTCHA 체크 완료 여부
      recaptchaResponse: '', // reCAPTCHA 응답 토큰
    };
  },
  computed: {
    ...mapState(['lang']),
  },
  mounted() {
    /** 초기화 */
    this.initRecaptcha();
  },
  methods: {
    /** reCAPTCHA 초기화 */
    initRecaptcha() {
      const srcEl = document.getElementById(RECAPTCHA_SCRIPT_ID);
      if (srcEl) {
        srcEl.remove();
      }

      const script = document.createElement('script');
      const src = RECAPTCHA_SCRIPT_SRC;
      const lang = this.lang ? `&hl=${this.lang}` : '';

      script.setAttribute('id', RECAPTCHA_SCRIPT_ID);
      script.setAttribute('src', `${src}?render=explicit${lang}`);
      script.setAttribute('async', '');
      script.setAttribute('defer', '');

      document.head.appendChild(script);

      script.onload = () => this.checkGrecaptcha();
      script.onerror = () => this.handleError();
    },
    /** reCAPTCHA 초기화 완료 여부 확인 */
    checkGrecaptcha() {
      const checkGrecaptcha = setInterval(() => {
        if (!window.grecaptcha) return;

        clearInterval(checkGrecaptcha);
        this.renderRecaptcha();
      }, 100);

      setTimeout(() => {
        clearInterval(checkGrecaptcha);
        if (window.grecaptcha) return;

        this.handleError();
      }, 10000);
    },
    /** reCAPTCHA 렌더링 */
    renderRecaptcha() {
      if (!window.grecaptcha || !window.grecaptcha?.render) {
        this.handleError();
      }

      window.grecaptcha.render(this.$refs.recaptcha, {
        sitekey: this.siteKey,
        callback: this.onCaptchaChange,
        'expired-callback': this.onCaptchaExpired,
      });
    },
    /** reCAPTCHA 체크박스 상태가 통과될 때 호출되는 메서드 */
    async onCaptchaChange(response) {
      // console.log(`onCaptchaChange`);
      /** reCAPTCHA 응답 토큰 저장 */
      this.recaptchaResponse = response;

      if (response) {
        const {
          data: { result },
        } = await this.$axios.post(`${process.env.VUE_APP_TEST_SERVER}/recaptcha/verify`, {
          token: response,
        });
        // console.log(`onCaptchaChange result: ${result}`);

        this.captchaVerified = result;
        this.verify(this.captchaVerified);
        // console.log('(Recaptcha) captchaVerified: ', this.captchaVerified);
      }
    },
    /** reCAPTCHA 체크박스 시간 만료 시 동작 함수 */
    onCaptchaExpired() {
      this.captchaVerified = false;
      this.verify(this.captchaVerified);
      // console.log('CAPTCHA expired!');
    },
    /** reCAPTCHA 에러 처리
     * @param {boolean} retry - 재시도 여부 기본값 true
     */
    handleError(retry = true) {
      const error = new ReCaptchaError('Failed to load script');
      if (retry) this.initRecaptcha();

      throw error;
    },
  },
};
</script>

<style lang="scss" scoped>
.g-recaptcha {
  transform-origin: top;
  margin-top: 20px;

  @media screen and (max-width: 470px) {
    width: 0;
    height: 60px;
    transform: scale(0.8);
    -webkit-transform: scale(0.8);
    margin-top: 12px;
  }

  &.embed {
    @media screen and (max-width: 470px) {
      width: 0;
      height: 40px;
      transform: scale(0.7);
      -webkit-transform: scale(0.7);
      margin-top: 8px;

      &.en {
        width: 0;
        height: 36px;
        transform: scale(0.65);
        -webkit-transform: scale(0.65);
      }
    }
    @media screen and (max-width: 359px) {
      width: 0;
      height: 32px;
      transform: scale(0.6);
      -webkit-transform: scale(0.6);

      &.en {
        width: 0;
        height: 24px;
        transform: scale(0.55);
        -webkit-transform: scale(0.55);
      }
    }
  }
}
</style>
